import { useCallback, useEffect, useState } from 'react'
import { Building2, Loader2, X, SquarePen, Trash2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../../auth/store/authStore'
import { propertiesService } from '../../../services/propertiesService'
import type { Property, PropertyType, CurrencyCode } from '../../../types/api.types'
import { CURRENCY_OPTIONS } from '../constants/adminConstants'
import { EmptyState } from './EmptyState'
import { LoadErrorState } from '../../../components/LoadErrorState'

interface PropertyFormState {
  name: string
  code: string
  propertyType: PropertyType
  currency: CurrencyCode
}

const EMPTY_FORM: PropertyFormState = {
  name: '',
  code: '',
  propertyType: 'RESTAURANT',
  currency: 'PKR',
}

export function PropertiesTab(): JSX.Element {
  const authUser = useAuthStore((s) => s.user)

  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [editingPropertyId, setEditingPropertyId] = useState<number | null>(null)
  const [form, setForm] = useState<PropertyFormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await propertiesService.getAll()
      setProperties(data)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  function resetForm() {
    setForm(EMPTY_FORM)
    setEditingPropertyId(null)
  }

  function openCreateModal() {
    resetForm()
    setShowModal(true)
  }

  function openEditModal(property: Property) {
    setForm({
      name: property.name,
      code: property.code,
      propertyType: property.property_type as PropertyType,
      currency: property.currency as CurrencyCode,
    })
    setEditingPropertyId(property.id)
    setShowModal(true)
  }

  async function handleSubmit() {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error('Name and Code are required')
      return
    }
    setSubmitting(true)
    try {
      if (!editingPropertyId) {
        await propertiesService.create({
          name: form.name.trim(),
          code: form.code.trim(),
          property_type: form.propertyType,
          currency: form.currency,
          organization_id: authUser?.organization_id ?? 0,
        })
        toast.success('Property created successfully')
      } else {
        await propertiesService.update(editingPropertyId, {
          name: form.name.trim(),
          code: form.code.trim(),
          property_type: form.propertyType,
          currency: form.currency,
        })
        toast.success('Property updated successfully')
      }
      setShowModal(false)
      resetForm()
      await loadData()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save property'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Are you sure you want to delete this property? This action cannot be undone and will affect all associated data.')) return
    try {
      await propertiesService.delete(id)
      toast.success('Property deleted successfully')
      await loadData()
    } catch {
      toast.error('Failed to delete property')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading properties...</span>
      </div>
    )
  }

  if (loadError) {
    return <LoadErrorState message="Couldn't load properties" onRetry={loadData} />
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Manage Properties</h2>
        <button
          type="button"
          onClick={openCreateModal}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-70"
        >
          + Create Property
        </button>
      </div>

      {properties.length === 0 ? (
        <EmptyState icon={Building2} message="No properties found" />
      ) : (
        <>
        {/* Mobile card list */}
        <div className="space-y-3 md:hidden">
          {properties.map(p => (
            <div key={p.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-gray-900">{p.name}</p>
                  <p className="truncate text-[13px] text-gray-500">Code: {p.code}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => openEditModal(p)} aria-label={`Edit ${p.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-brand-green hover:bg-brand-green/10">
                    <SquarePen size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(p.id)} aria-label={`Delete ${p.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-red-400 hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px]">
                <span className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
                  {p.property_type.charAt(0) + p.property_type.slice(1).toLowerCase()}
                </span>
                <span className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700">{p.currency}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">ID</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Code</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Property Name</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Currency</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Type</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p, idx) => (
                <tr key={p.id} className="border-t border-gray-100">
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{String(idx + 1).padStart(2, '0')}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{p.code}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-900">{p.name}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{p.currency}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{p.property_type.charAt(0) + p.property_type.slice(1).toLowerCase()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <button type="button" onClick={() => openEditModal(p)} className="text-brand-green hover:text-brand-green/70">
                        <SquarePen size={16} />
                      </button>
                      <button type="button" onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingPropertyId ? 'Edit Property Information' : 'Create Property'}
              </h2>
              <button type="button" onClick={() => { setShowModal(false); resetForm() }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="Enter your full name."
                />
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Code*</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="e.g. PROP001"
                />
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Organization Type*</label>
                <select
                  value={form.propertyType}
                  onChange={e => setForm({ ...form, propertyType: e.target.value as PropertyType })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                >
                  <option value="RESTAURANT">Restaurant</option>
                  <option value="HOTEL">Hotel</option>
                  <option value="SECURITY">Security</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Currency*</label>
                <select
                  value={form.currency}
                  onChange={e => setForm({ ...form, currency: e.target.value as CurrencyCode })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                >
                  {CURRENCY_OPTIONS.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>

            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : editingPropertyId ? 'Edit' : 'Create property'}
              </button>
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm() }}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
